import React, { useState } from 'react';
import {
  Dialog,
  TextField,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  alpha,
  CircularProgress,
  useMediaQuery,
  useTheme as useMuiTheme
} from '@mui/material';
import {
  Close,
  CloudUpload,
  Group,
  Add
} from '@mui/icons-material';
import { useTheme as useCustomTheme } from '../../context/ThemeContext';
import toast from 'react-hot-toast';
import { communityApi } from '../../services/communityApi';

// Import your logo
import logo from '../../assets/Text-WFHCafe_WObg.png';

interface CreateCommunityModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (community: any) => void;
}

const CreateCommunityModal: React.FC<CreateCommunityModalProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const muiTheme = useMuiTheme();
  const { mode } = useCustomTheme();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'other',
    tags: [] as string[],
    rules: [] as string[]
  });
  const [tagInput, setTagInput] = useState('');
  const [ruleInput, setRuleInput] = useState('');
  const [bannerImage, setBannerImage] = useState<File | null>(null);
  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string>('');
  const [profilePreview, setProfilePreview] = useState<string>('');

  const isMobile = useMediaQuery('(max-width:600px)');
  const isTablet = useMediaQuery('(max-width:900px)');
  const isDark = mode === 'dark';
  const goldColor = '#C4B66F';
  const deepGreen = '#051814';
  const lokiGradient = 'linear-gradient(135deg, #051814 0%, #0F3B2E 100%)';
  const goldGradient = `linear-gradient(135deg, ${goldColor} 0%, #D4C690 100%)`;

  // Glass effect styling
  const glassEffect = {
    backdropFilter: 'blur(20px)',
    background: isDark
      ? 'linear-gradient(135deg, rgba(5, 24, 20, 0.98) 0%, rgba(15, 59, 46, 0.98) 100%)'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 250, 250, 0.98) 100%)',
    border: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
    boxShadow: isDark
      ? `0 25px 50px ${alpha('#000000', 0.5)}`
      : `0 25px 50px ${alpha(deepGreen, 0.15)}`,
  };

  const modalStyle = {
    borderRadius: '16px',
    ...glassEffect,
    width: isMobile ? 'calc(100vw - 32px)' : (isTablet ? '600px' : '700px'),
    maxHeight: isMobile ? 'calc(100vh - 250px)' : '80vh',
    margin: 'auto',
    overflow: 'hidden',
    padding: 0,
    // Hide scrollbar but allow scrolling
    '& .MuiDialogContent-root': {
      overflowY: 'auto',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
      msOverflowStyle: 'none',
      scrollbarWidth: 'none',
    },
  };

  // Input field styles with gold focus border
  const inputFieldStyle = {
    borderRadius: '12px',
    bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(5, 24, 20, 0.02)',
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: isDark ? 'rgba(196, 182, 111, 0.3)' : 'rgba(5, 24, 20, 0.1)',
        borderWidth: '1px',
        borderRadius: '12px'
      },
      '&:hover fieldset': { 
        borderColor: goldColor,
        borderWidth: '1px'
      },
      '&.Mui-focused fieldset': { 
        borderColor: goldColor,
        borderWidth: '2px'
      }
    },
    '& .MuiInputLabel-root': {
      color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)',
      fontWeight: 600
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: goldColor,
      fontWeight: 700
    }
  };

  const gradientButtonStyle = {
    background: isDark ? goldGradient : lokiGradient,
    color: isDark ? deepGreen : goldColor,
    borderRadius: '50px',
    fontWeight: 800,
    textTransform: 'none',
    fontSize: isMobile ? '0.9rem' : '1rem',
    padding: isMobile ? '10px 20px' : '12px 32px',
    letterSpacing: '-0.02em',
    border: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
    boxShadow: isDark
      ? `0 8px 32px ${alpha(goldColor, 0.25)}`
      : `0 8px 32px ${alpha(deepGreen, 0.2)}`,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: isDark
        ? `0 12px 40px ${alpha(goldColor, 0.35)}`
        : `0 12px 40px ${alpha(deepGreen, 0.3)}`,
      background: isDark
        ? `linear-gradient(135deg, ${alpha(goldColor, 0.9)} 0%, #E5D9A8 100%)`
        : 'linear-gradient(135deg, #051814 0%, #1D5C4A 100%)',
    },
    '&:active': {
      transform: 'translateY(0)',
    }
  };

  const categories = [
    { value: 'cafe-discussion', label: 'Cafe Discussion' },
    { value: 'remote-work', label: 'Remote Work' },
    { value: 'digital-nomads', label: 'Digital Nomads' },
    { value: 'food-drinks', label: 'Food & Drinks' },
    { value: 'coffee-lovers', label: 'Coffee Lovers' },
    { value: 'study-groups', label: 'Study Groups' },
    { value: 'networking', label: 'Networking' },
    { value: 'events', label: 'Events' },
    { value: 'reviews', label: 'Reviews' },
    { value: 'other', label: 'Other' }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTagAdd = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim().toLowerCase())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim().toLowerCase()]
      }));
      setTagInput('');
    }
  };

  const handleTagDelete = (tagToDelete: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToDelete)
    }));
  };

  const handleRuleAdd = () => {
    if (ruleInput.trim() && !formData.rules.includes(ruleInput.trim())) {
      setFormData(prev => ({
        ...prev,
        rules: [...prev.rules, ruleInput.trim()]
      }));
      setRuleInput('');
    }
  };

  const handleRuleDelete = (ruleToDelete: string) => {
    setFormData(prev => ({
      ...prev,
      rules: prev.rules.filter(rule => rule !== ruleToDelete)
    }));
  };

  const handleBannerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error('Image size should be less than 5MB');
        return;
      }
      setBannerImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setBannerPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { // 2MB limit for profile
        toast.error('Profile picture size should be less than 2MB');
        return;
      }
      setProfilePicture(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeBanner = () => {
    setBannerImage(null);
    setBannerPreview('');
  };

  const removeProfile = () => {
    setProfilePicture(null);
    setProfilePreview('');
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.description || !formData.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.name.length < 3 || formData.name.length > 100) {
      toast.error('Community name must be between 3 and 100 characters');
      return;
    }

    if (formData.description.length > 500) {
      toast.error('Description must be less than 500 characters');
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('tags', JSON.stringify(formData.tags));
      formDataToSend.append('rules', JSON.stringify(formData.rules));

      if (bannerImage) {
        formDataToSend.append('bannerImage', bannerImage);
      }

      if (profilePicture) {
        formDataToSend.append('profilePicture', profilePicture);
      }

      const response = await communityApi.createCommunity(formDataToSend);

      if (response.success) {
        toast.success('Community created successfully!');
        onSuccess(response.community);
        handleClose();
      } else {
        toast.error(response.error || 'Failed to create community');
      }
    } catch (error: any) {
      console.error('Create community error:', error);
      toast.error(error.message || 'Failed to create community');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      category: 'other',
      tags: [],
      rules: []
    });
    setTagInput('');
    setRuleInput('');
    setBannerImage(null);
    setProfilePicture(null);
    setBannerPreview('');
    setProfilePreview('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: modalStyle,
      }}
      scroll="paper"
    >
      {/* Single Content Container with Logo */}
      <Box sx={{
        p: isMobile ? 3 : 4,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
      }}>
        {/* Logo and Close Button */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: isMobile ? 3 : 4,
        }}>
          <Box
            component="img"
            src={logo}
            alt="WFHCafe Logo"
            sx={{
              height: isMobile ? 35 : 45,
              width: 'auto',
              filter: isDark ? 'brightness(1.1)' : 'none',
            }}
          />
          
          <IconButton 
            onClick={handleClose} 
            sx={{ 
              color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
              backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              padding: isMobile ? '6px' : '8px',
              '&:hover': { 
                color: goldColor,
                backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(5,24,20,0.1)',
              }
            }}
          >
            <Close fontSize={isMobile ? "medium" : "large"} />
          </IconButton>
        </Box>

        {/* Title */}
        <Typography 
          variant={isMobile ? "h5" : "h4"} 
          sx={{ 
            fontWeight: 800,
            letterSpacing: '-0.02em',
            color: isDark ? '#fff' : deepGreen,
            mb: 1,
            fontSize: isMobile ? '1.5rem' : '2rem',
          }}
        >
          Create Community
        </Typography>
        
        <Typography 
          variant="body1" 
          sx={{ 
            color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
            mb: isMobile ? 3 : 4,
            fontSize: isMobile ? '0.9rem' : '1rem',
          }}
        >
          Build your own space for like-minded people
        </Typography>

        {/* Profile Picture and Banner Upload */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexDirection: isMobile ? 'column' : 'row' }}>
          {/* Profile Picture Upload */}
          <Box
  sx={{
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: isMobile ? 'center' : 'flex-start',
    textAlign: isMobile ? 'center' : 'left',
  }}
>
            <Typography variant="subtitle2" sx={{ 
              mb: 1,
              fontWeight: 700,
              color: isDark ? '#fff' : deepGreen,
              fontSize: '0.9rem',
            }}>
              Profile Picture
            </Typography>
            <Box sx={{ position: 'relative', display: 'inline-block' }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="profile-upload"
                type="file"
                onChange={handleProfileChange}
              />
              <label htmlFor="profile-upload">
                <Box
                  sx={{
                    height: 100,
                    width: 100,
                    borderRadius: '50%',
                    bgcolor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(5, 24, 20, 0.02)',
                    border: `2px dashed ${alpha(goldColor, 0.3)}`,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: goldColor,
                      bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(5, 24, 20, 0.04)',
                      transform: 'scale(1.02)',
                    }
                  }}
                >
                  {profilePreview ? (
                    <img
                      src={profilePreview}
                      alt="Profile preview"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '50%'
                      }}
                    />
                  ) : (
                    <Box sx={{ textAlign: 'center', p: 1 }}>
                      <CloudUpload sx={{ fontSize: 24, color: goldColor, mb: 0.5 }} />
                      <Typography variant="caption" sx={{ 
                        color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(5,24,20,0.5)',
                        fontSize: '0.7rem',
                        display: 'block'
                      }}>
                        Add
                      </Typography>
                    </Box>
                  )}
                </Box>
              </label>
              {profilePreview && (
                <IconButton
                  size="small"
                  onClick={removeProfile}
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    backgroundColor: isDark ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.9)',
                    border: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
                    width: 24,
                    height: 24,
                    '&:hover': {
                      backgroundColor: isDark ? 'rgba(255,0,0,0.3)' : 'rgba(255,0,0,0.1)',
                      color: '#ff4444',
                    }
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>
          
          {/* Banner Image Upload */}
          <Box sx={{ flex: 2 }}>
            <Typography variant="subtitle2" sx={{ 
              mb: 1,
              fontWeight: 700,
              color: isDark ? '#fff' : deepGreen,
              fontSize: '0.9rem',
            }}>
              Banner Image
            </Typography>
            <Box sx={{ position: 'relative' }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="banner-upload"
                type="file"
                onChange={handleBannerChange}
              />
              <label htmlFor="banner-upload">
                <Box
                  sx={{
                    height: 100,
                    borderRadius: '12px',
                    bgcolor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(5, 24, 20, 0.02)',
                    border: `2px dashed ${alpha(goldColor, 0.3)}`,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: goldColor,
                      bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(5, 24, 20, 0.04)',
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${alpha(goldColor, 0.1)}`,
                    }
                  }}
                >
                  {bannerPreview ? (
                    <img
                      src={bannerPreview}
                      alt="Banner preview"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '12px'
                      }}
                    />
                  ) : (
                    <Box sx={{ textAlign: 'center', p: 1 }}>
                      <CloudUpload sx={{ fontSize: 24, color: goldColor, mb: 0.5 }} />
                      <Typography variant="caption" sx={{ 
                        color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(5,24,20,0.5)',
                        fontSize: '0.7rem',
                        display: 'block'
                      }}>
                        1200x300 recommended
                      </Typography>
                    </Box>
                  )}
                </Box>
              </label>
              {bannerPreview && (
                <IconButton
                  size="small"
                  onClick={removeBanner}
                  sx={{
                    position: 'absolute',
                    top: -10,
                    right: -10,
                    backgroundColor: isDark ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.9)',
                    border: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
                    width: 28,
                    height: 28,
                    '&:hover': {
                      backgroundColor: isDark ? 'rgba(255,0,0,0.3)' : 'rgba(255,0,0,0.1)',
                      color: '#ff4444',
                    }
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>

        {/* Community Name */}
        <TextField
          fullWidth
          label="Community Name"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          required
          sx={{ mb: 3, ...inputFieldStyle }}
          placeholder="e.g., Digital Nomads Hub"
          helperText="Choose a unique name for your community"
          FormHelperTextProps={{
            sx: {
              color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(5,24,20,0.5)',
              fontSize: '0.85rem',
            }
          }}
        />

        {/* Description */}
        <TextField
          fullWidth
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          required
          multiline
          rows={3}
          sx={{ mb: 3, ...inputFieldStyle }}
          placeholder="Describe what your community is about..."
          helperText="Briefly describe your community's purpose"
          FormHelperTextProps={{
            sx: {
              color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(5,24,20,0.5)',
              fontSize: '0.85rem',
            }
          }}
        />

        {/* Category */}
        <FormControl fullWidth sx={{ mb: 3, ...inputFieldStyle }}>
          <InputLabel>Category</InputLabel>
          <Select
            name="category"
            value={formData.category}
            onChange={handleSelectChange}
            label="Category"
            MenuProps={{
              PaperProps: {
                sx: {
                  borderRadius: '12px',
                  bgcolor: isDark ? deepGreen : '#FFFFFF',
                  border: `1px solid ${alpha(goldColor, 0.2)}`,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                  '& .MuiMenuItem-root': {
                    color: isDark ? '#fff' : deepGreen,
                    fontSize: '0.9rem',
                    '&:hover': {
                      bgcolor: alpha(goldColor, 0.1),
                    },
                    '&.Mui-selected': {
                      bgcolor: alpha(goldColor, 0.2),
                      color: goldColor,
                      fontWeight: 700,
                      '&:hover': {
                        bgcolor: alpha(goldColor, 0.3),
                      }
                    }
                  }
                }
              }}
            }
          >
            {categories.map((category) => (
              <MenuItem key={category.value} value={category.value}>
                {category.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Tags */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ 
            mb: 1.5, 
            fontWeight: 800,
            color: isDark ? '#fff' : deepGreen,
            fontSize: '0.95rem',
            letterSpacing: '-0.02em'
          }}>
            Tags
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Add tags (e.g., remote, coffee, workspace)..."
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleTagAdd();
                }
              }}
              sx={{
                borderRadius: '12px',
                bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(5, 24, 20, 0.02)',
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: isDark ? 'rgba(196, 182, 111, 0.3)' : 'rgba(5, 24, 20, 0.1)',
                    borderWidth: '1px',
                    borderRadius: '12px'
                  },
                  '&:hover fieldset': { 
                    borderColor: goldColor,
                    borderWidth: '1px'
                  },
                  '&.Mui-focused fieldset': { 
                    borderColor: goldColor,
                    borderWidth: '2px'
                  }
                },
                '& .MuiInputLabel-root': {
                  color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)',
                  fontWeight: 600
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: goldColor,
                  fontWeight: 700
                }
              }}
              InputProps={{
                sx: {
                  color: isDark ? '#fff' : deepGreen,
                  fontSize: '0.9rem',
                }
              }}
            />
            <Button
              variant="outlined"
              onClick={handleTagAdd}
              sx={{ 
                borderRadius: '12px',
                borderColor: alpha(goldColor, 0.3),
                color: goldColor,
                fontWeight: 700,
                minWidth: '80px',
                fontSize: '0.9rem',
                height: '40px',
                '&:hover': {
                  borderColor: goldColor,
                  bgcolor: alpha(goldColor, 0.1)
                }
              }}
            >
              Add
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {formData.tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                onDelete={() => handleTagDelete(tag)}
                size="small"
                sx={{
                  bgcolor: alpha(goldColor, 0.15),
                  color: goldColor,
                  fontWeight: 700,
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  '& .MuiChip-deleteIcon': {
                    color: goldColor,
                    fontSize: '1rem',
                    '&:hover': {
                      color: isDark ? '#fff' : deepGreen,
                    }
                  }
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Rules */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ 
            mb: 1.5, 
            fontWeight: 800,
            color: isDark ? '#fff' : deepGreen,
            fontSize: '0.95rem',
            letterSpacing: '-0.02em'
          }}>
            Community Rules
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Add a rule..."
              value={ruleInput}
              onChange={(e) => setRuleInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleRuleAdd();
                }
              }}
              sx={{
                borderRadius: '12px',
                bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(5, 24, 20, 0.02)',
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: isDark ? 'rgba(196, 182, 111, 0.3)' : 'rgba(5, 24, 20, 0.1)',
                    borderWidth: '1px',
                    borderRadius: '12px'
                  },
                  '&:hover fieldset': { 
                    borderColor: goldColor,
                    borderWidth: '1px'
                  },
                  '&.Mui-focused fieldset': { 
                    borderColor: goldColor,
                    borderWidth: '2px'
                  }
                },
                '& .MuiInputLabel-root': {
                  color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)',
                  fontWeight: 600
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: goldColor,
                  fontWeight: 700
                }
              }}
              InputProps={{
                sx: {
                  color: isDark ? '#fff' : deepGreen,
                  fontSize: '0.9rem',
                }
              }}
            />
            <Button
              variant="outlined"
              onClick={handleRuleAdd}
              sx={{ 
                borderRadius: '12px',
                borderColor: alpha(goldColor, 0.3),
                color: goldColor,
                fontWeight: 700,
                minWidth: '80px',
                fontSize: '0.9rem',
                height: '40px',
                '&:hover': {
                  borderColor: goldColor,
                  bgcolor: alpha(goldColor, 0.1)
                }
              }}
            >
              Add
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {formData.rules.map((rule, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: '12px',
                  bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(5, 24, 20, 0.02)',
                  border: `1px solid ${alpha(goldColor, 0.1)}`,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: alpha(goldColor, 0.3),
                    bgcolor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(5, 24, 20, 0.04)',
                  }
                }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    bgcolor: alpha(goldColor, 0.2),
                    color: goldColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    fontSize: '0.85rem',
                    flexShrink: 0
                  }}
                >
                  {index + 1}
                </Box>
                <Typography variant="body2" sx={{ 
                  flex: 1,
                  color: isDark ? 'rgba(255,255,255,0.9)' : 'rgba(5,24,20,0.9)',
                  fontSize: '0.9rem',
                  lineHeight: 1.3
                }}>
                  {rule}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleRuleDelete(rule)}
                  sx={{ 
                    color: '#ff4444',
                    padding: '4px',
                    '&:hover': {
                      bgcolor: alpha('#ff4444', 0.1)
                    }
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Buttons at Bottom */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mt: 'auto',
          pt: 3,
          flexWrap: isMobile ? 'wrap' : 'nowrap',
          gap: isMobile ? 2 : 0,
        }}>
          <Button
            onClick={handleClose}
            sx={{
              color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)',
              fontWeight: 700,
              borderRadius: '50px',
              px: isMobile ? 3 : 4,
              py: isMobile ? 0.75 : 1,
              fontSize: isMobile ? '0.9rem' : '1rem',
              textTransform: 'none',
              minWidth: isMobile ? '120px' : 'auto',
              '&:hover': {
                bgcolor: alpha(goldColor, 0.05)
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{
              ...gradientButtonStyle,
              minWidth: isMobile ? '140px' : 'auto',
              '&:disabled': {
                background: isDark ? 'rgba(196, 182, 111, 0.3)' : 'rgba(5, 24, 20, 0.3)',
                color: isDark ? 'rgba(5, 24, 20, 0.5)' : 'rgba(196, 182, 111, 0.5)',
                transform: 'none',
                boxShadow: 'none',
              }
            }}
          >
            {loading ? 'Creating...' : 'Create Community'}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default CreateCommunityModal;